import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { BlogPost, BlogState } from '@/types/types';
import { blogService } from '@/services/blogService';

// Define action types
type BlogAction = 
  | { type: 'FETCH_POSTS_START' }
  | { type: 'FETCH_POSTS_SUCCESS'; payload: { posts: BlogPost[], recentPosts: BlogPost[] } }
  | { type: 'FETCH_POSTS_ERROR'; payload: Error }
  | { type: 'SELECT_POST'; payload: BlogPost | null }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_CURRENT_PAGE'; payload: number };

// Initial state
const initialState: BlogState = {
  posts: [],
  recentPosts: [],
  selectedPost: null,
  isLoading: false,
  error: null,
  searchQuery: '',
  currentPage: 1,
  totalPages: 1,
  postsPerPage: 6
};

// Create reducer
const blogReducer = (state: BlogState, action: BlogAction): BlogState => {
  switch (action.type) {
    case 'FETCH_POSTS_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'FETCH_POSTS_SUCCESS':
      return {
        ...state,
        posts: action.payload.posts,
        recentPosts: action.payload.recentPosts,
        isLoading: false,
        error: null,
        totalPages: Math.ceil(action.payload.posts.length / state.postsPerPage)
      };
    case 'FETCH_POSTS_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'SELECT_POST':
      return {
        ...state,
        selectedPost: action.payload
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload,
        currentPage: 1 // Reset to first page when searching
      };
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload
      };
    default:
      return state;
  }
};

// Create context
const BlogContext = createContext<{
  state: BlogState;
  dispatch: React.Dispatch<BlogAction>;
}>({
  state: initialState,
  dispatch: () => null
});

// Create provider
export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(blogReducer, initialState);

  useEffect(() => {
    const fetchPosts = async () => {
      dispatch({ type: 'FETCH_POSTS_START' });
      try {
        // Fetch data using the blog service
        const [posts, recentPosts] = await Promise.all([
          blogService.getAllPosts(),
          blogService.getRecentPosts(3)
        ]);
        
        dispatch({ 
          type: 'FETCH_POSTS_SUCCESS', 
          payload: { posts, recentPosts } 
        });
      } catch (error) {
        dispatch({ 
          type: 'FETCH_POSTS_ERROR', 
          payload: error as Error 
        });
      }
    };

    fetchPosts();
  }, []);

  return (
    <BlogContext.Provider value={{ state, dispatch }}>
      {children}
    </BlogContext.Provider>
  );
};

// Create hook for using the context
export const useBlog = () => useContext(BlogContext);